import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-black flex items-center justify-center p-4">
                    <div className="max-w-md w-full bg-[#0a0a12] border border-white/10 rounded-2xl p-8 text-center shadow-2xl relative overflow-hidden">

                        {/* Background Glow */}
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-orange-500/20 rounded-full blur-[60px] pointer-events-none" />

                        <div className="relative z-10 flex flex-col items-center">
                            <div className="w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center mb-6 border border-orange-500/20">
                                <AlertTriangle className="text-orange-500" size={32} />
                            </div>

                            <h1 className="text-2xl font-bold text-white mb-2 font-['Outfit']">
                                System Malfunction
                            </h1>

                            <p className="text-slate-400 mb-8 max-w-sm mx-auto leading-relaxed">
                                Our orbit check detected an unexpected anomaly. The navigation systems need a quick reset.
                            </p>

                            {/* Error Details (Optional, for debugging dev builds) */}
                            {import.meta.env.DEV && this.state.error && (
                                <div className="w-full bg-black/40 border border-white/5 rounded-lg p-4 mb-6 text-left overflow-auto max-h-32">
                                    <p className="text-xs text-red-400 font-mono break-all">
                                        {this.state.error.toString()}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={this.handleReload}
                                className="group flex items-center gap-2 px-6 py-3 bg-white text-black font-bold uppercase tracking-widest text-xs rounded-full hover:bg-slate-200 transition-colors"
                            >
                                <RefreshCw size={14} className="group-hover:rotate-180 transition-transform duration-500" />
                                <span>Re-Initialize</span>
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
